// Fires a synchronous Gemini analysis as soon as the operator picks a file,
// instead of waiting for form submit. Renders the brief in a collapsible
// preview and writes it to a hidden input so the create action reuses the
// result rather than re-analysing.
//
// Markup expected (inside the controller's scope):
//   <input type="file" data-gemini-upload-target="file" data-action="change->gemini-upload#analyse">
//   <div data-gemini-upload-target="status" hidden></div>
//   <details data-gemini-upload-target="resultWrap" hidden>
//     <summary>...</summary>
//     <pre data-gemini-upload-target="result"></pre>
//   </details>
//   <input type="hidden" name="..." data-gemini-upload-target="hiddenAnalysis">
//
// Required values:
//   data-gemini-upload-kind-value="editorial_background"  (or another ArtDirectionReference kind)
//   data-gemini-upload-endpoint-value="<%= analyze_upload_operators_single_generations_path %>"
//
// Optional values:
//   data-gemini-upload-name-value="Composition reference"
//
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["file", "status", "result", "resultWrap", "hiddenAnalysis"]
  static values = { kind: String, endpoint: String, name: String }

  analyse() {
    const file = this.fileTarget.files && this.fileTarget.files[0]
    if (!file) {
      this._setStatus("Pick a file first.")
      return
    }

    this._setStatus("Analysing with Gemini…")
    if (this.hasResultWrapTarget) this.resultWrapTarget.hidden = true
    if (this.hasHiddenAnalysisTarget) this.hiddenAnalysisTarget.value = ""

    const formData = new FormData()
    formData.append("file", file)
    formData.append("kind", this.kindValue)
    if (this.hasNameValue && this.nameValue) formData.append("name", this.nameValue)

    const csrf = document.querySelector('meta[name="csrf-token"]')
    const headers = csrf ? { "X-CSRF-Token": csrf.content } : {}

    fetch(this.endpointValue, { method: "POST", headers: headers, body: formData, credentials: "same-origin" })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`)
        return data
      })
      .then((data) => {
        const analysis = (data.analysis || "").toString()
        if (analysis.length === 0) {
          this._setStatus("Gemini returned no analysis — submit will retry server-side.")
          return
        }
        this._setStatus(`✓ Analysed (${analysis.length} chars). Will be appended to the prompt on submit.`)
        if (this.hasResultTarget) {
          this.resultTarget.textContent = analysis
        }
        if (this.hasResultWrapTarget) {
          this.resultWrapTarget.hidden = false
        }
        if (this.hasHiddenAnalysisTarget) {
          this.hiddenAnalysisTarget.value = analysis
        }
      })
      .catch((err) => {
        this._setStatus(`Analysis failed: ${err.message}. Submit will retry server-side.`)
      })
  }

  _setStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.hidden = false
  }
}
